import {
  Component,
  Injector,
  OnInit,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { Role } from '../../../../types/roles'
import { MatOptionModule } from '@angular/material/core'
import { MatSelectModule } from '@angular/material/select'
import { MatIcon } from '@angular/material/icon'
import { MatButtonModule } from '@angular/material/button'
import { FormBuilder } from '@angular/forms'
import { Validators } from '@angular/forms'
import { RouteService } from '../../../../services/route.service'
import { ROUTES } from '../../../../constants/routes'
import { RoleService } from '../../../../services/role.service'
import { Dialog } from '@angular/cdk/dialog'
import { UserService } from '../../../../services/user.service'
import { User, UserWithCompany } from '../../../../types/users'
import { ApiError } from '../../../../types/apiError'
import { firstValueFrom } from 'rxjs'
import { ErrorService } from '../../../../services/error.service'
import { DialogService } from '../../../../services/dialog.service'
import { SpinnerService } from '../../../../services/spinner.service'
import { AuthService } from '../../../../services/auth.service'
import { USER_ROLES, USER_ROLES_WITH_COMPANY } from '../../../../constants/auth'
import { Company } from '../../../../types/company'
import { CompanyService } from '../../../../services/company.service'
import { TranslationService } from '../../../../services/translation.service'

@Component({
  selector: 'app-user-create',
  imports: [
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    MatOptionModule,
    MatSelectModule,
    MatButtonModule,
    MatIcon,
  ],
  templateUrl: './user-create.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './user-create.component.scss',
})
export class UserCreateComponent implements OnInit {
  injector = inject(Injector)
  formBuilder = inject(FormBuilder)
  routeService = inject(RouteService)
  roleService = inject(RoleService)
  userService = inject(UserService)
  dialog = inject(Dialog)
  errorService = inject(ErrorService)
  dialogService = inject(DialogService)
  spinnerService = inject(SpinnerService)
  authService = inject(AuthService)
  companyService = inject(CompanyService)
  translationService = inject(TranslationService)

  roles: Array<Role> = []
  companies: Array<Company> = []

  isMasterUser = false
  shouldHaveCompany = false

  userForm = this.formBuilder.group({
    firstName: ['', [Validators.required]],
    lastName: ['', [Validators.required]],
    email: ['', [Validators.required, Validators.email]],
    roleId: [null, [Validators.required]],
    companyId: [null],
  })

  roleMap = new Map<string, string>()

  async ngOnInit(): Promise<void> {
    try {
      this.spinnerService.startSpinner()
      const currentRoute = this.routeService.getUserRoutes(
        ROUTES.VITE_MARKETPLACE.title,
        `/admin/${ROUTES.USERS_CREATE.path}`,
      )
      this.routeService.setRoute(currentRoute)

      this.isMasterUser = this.authService.validateRole(USER_ROLES.MASTER)
      if (this.isMasterUser) {
        const companiesResponse = await firstValueFrom(
          this.companyService.getAll(),
        )
        this.companies = companiesResponse
      }

      const rolesResponse = await firstValueFrom(this.roleService.getAll())
      this.roles = rolesResponse

      for (const role of this.roles) {
        this.roleMap.set(role.id, role.name)
      }

      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Submits form data to api
   */
  async onSubmit() {
    if (!this.userForm.valid) {
      return
    }

    try {
      this.spinnerService.startSpinner()
      const user: User = this.userForm.value as User
      if (this.isMasterUser && this.shouldHaveCompany) {
        const companyId = this.userForm.get('companyId')?.value
        if (!companyId) {
          throw Error('CompanyId must be provided')
        }
        const userWithCompany: UserWithCompany = {
          ...user,
          companyId,
        }
        await firstValueFrom(
          this.userService.createWithCompany(userWithCompany),
        )
      } else {
        await firstValueFrom(this.userService.create(user))
      }

      this.dialogService.showDialog(
        this.dialog,
        this.translationService.getUsersText(),
        this.translationService.getUserCreateSuccessMessage(),
      )

      this.spinnerService.stopSpinner()
    } catch (err) {
      this.spinnerService.stopSpinner()
      const apiErr = err as ApiError
      this.errorService.handleErr(this.dialogService, apiErr, this.dialog)
    }
  }

  /**
   * Resets form data
   */
  onReset() {
    this.userForm.reset()
  }

  /**
   * Function called on role change. Checks if company dropdown should be displayed
   */
  changeRole(): void {
    const selectedRoleId = this.userForm.get('roleId')?.value
    if (!selectedRoleId) {
      return
    }
    const roleName = this.roleMap.get(selectedRoleId)
    if (!roleName) {
      return
    }

    this.shouldHaveCompany = USER_ROLES_WITH_COMPANY.includes(roleName)

    if (this.shouldHaveCompany) {
      this.userForm.get('companyId')?.setValidators([Validators.required])
    } else {
      this.userForm.get('companyId')?.removeValidators(Validators.required)
    }
  }
}
